import * as React from "react"
import Link from "next/link"

import { cva, type VariantProps } from "class-variance-authority"
import { ChevronRight } from "lucide-react"

import { cn } from "../../cn"

/* ── CVA variants ─────────────────────────────────────────── */

const breadcrumbListVariants = cva([
  "flex flex-wrap items-center gap-1.5 text-sm text-coval-text-muted",
])

const breadcrumbItemVariants = cva(["inline-flex items-center gap-1.5"])

const breadcrumbLinkVariants = cva([
  "cursor-pointer text-coval-text-muted transition-colors duration-150",
  "hover:text-coval-text",
  " focus-visible:rounded-sm",
])

const breadcrumbPageVariants = cva(["text-coval-text font-semibold"])

const breadcrumbSeparatorVariants = cva([
  "text-coval-text-muted [&>svg]:h-3.5 [&>svg]:w-3.5",
])

/* ── Breadcrumb (nav root) ───────────────────────────────── */

export type BreadcrumbProps = React.ComponentPropsWithoutRef<"nav">

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ ...props }, ref) => <nav ref={ref} aria-label="Breadcrumb" {...props} />,
)

Breadcrumb.displayName = "Breadcrumb"

/* ── BreadcrumbList ──────────────────────────────────────── */

export interface BreadcrumbListProps
  extends
    React.ComponentPropsWithoutRef<"ol">,
    VariantProps<typeof breadcrumbListVariants> {}

const BreadcrumbList = React.forwardRef<HTMLOListElement, BreadcrumbListProps>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(breadcrumbListVariants(), className)}
      {...props}
    />
  ),
)

BreadcrumbList.displayName = "BreadcrumbList"

/* ── BreadcrumbItem ──────────────────────────────────────── */

export interface BreadcrumbItemProps
  extends
    React.ComponentPropsWithoutRef<"li">,
    VariantProps<typeof breadcrumbItemVariants> {}

const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(breadcrumbItemVariants(), className)}
      {...props}
    />
  ),
)

BreadcrumbItem.displayName = "BreadcrumbItem"

/* ── BreadcrumbLink ──────────────────────────────────────── */

/**
 * The navigation props a call site may need from `next/link`. The rest of the
 * surface is a plain anchor's, so an `href` stays a `string` rather than
 * widening to Link's `Url`.
 */
type BreadcrumbLinkNavigationProps = Partial<
  Pick<
    React.ComponentPropsWithoutRef<typeof Link>,
    "prefetch" | "replace" | "scroll"
  >
>

export type BreadcrumbLinkProps = Omit<
  React.ComponentPropsWithoutRef<"a">,
  "href"
> &
  BreadcrumbLinkNavigationProps &
  VariantProps<typeof breadcrumbLinkVariants> & {
    href: string
  }

/**
 * Breadcrumbs are in-app navigation, so an `href` routes through the Next
 * router. A bare `<a>` triggers a full document load, which remounts the app
 * shell and throws away the query cache. A crumb that acts in place rather than
 * navigating is a BreadcrumbButton, and the current page is a BreadcrumbPage.
 *
 * Prefetch is off by default, which suppresses the viewport prefetch only. The
 * proxy matches essentially every route, so a breadcrumb prefetch is an
 * authenticated data round-trip rather than a static chunk, and breadcrumbs sit
 * in the viewport on every page — otherwise every crumb would fetch on load
 * whether or not anyone clicks, and the parent paths `DefaultPageHeader`
 * synthesises do not all resolve.
 *
 * Link still prefetches on hover and touch whatever this prop says, and bypasses
 * its own dedupe when it does, so a pointer swept along a trail costs one
 * request per crumb per pass. Its handlers run after any we pass, so a call site
 * cannot suppress that. Pass `prefetch` to restore the load-time prefetch.
 */
const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ className, href, prefetch = false, replace, scroll, ...props }, ref) => {
    return (
      <Link
        ref={ref}
        href={href}
        prefetch={prefetch}
        replace={replace}
        scroll={scroll}
        className={cn(breadcrumbLinkVariants(), className)}
        {...props}
      />
    )
  },
)

BreadcrumbLink.displayName = "BreadcrumbLink"

/* ── BreadcrumbButton ────────────────────────────────────── */

type BreadcrumbButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>

/**
 * A crumb that changes state in place, such as stepping back out of a
 * dashboard drill-down, rather than navigating to a URL. Styled like a link.
 */
const BreadcrumbButton = React.forwardRef<
  HTMLButtonElement,
  BreadcrumbButtonProps
>(({ className, type = "button", ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn(breadcrumbLinkVariants(), className)}
    {...props}
  />
))

BreadcrumbButton.displayName = "BreadcrumbButton"

/* ── BreadcrumbPage ──────────────────────────────────────── */

export interface BreadcrumbPageProps
  extends
    React.ComponentPropsWithoutRef<"h1">,
    VariantProps<typeof breadcrumbPageVariants> {}

/**
 * The current page, last in the trail. It is the page's one `h1`: the shell's
 * header row names every page through its breadcrumb, so page content starts
 * its own headings at `h2`. Render it once per trail.
 */
const BreadcrumbPage = React.forwardRef<
  HTMLHeadingElement,
  BreadcrumbPageProps
>(({ className, children, ...props }, ref) => (
  <h1
    ref={ref}
    aria-current="page"
    className={cn(breadcrumbPageVariants(), className)}
    {...props}
  >
    {children}
  </h1>
))

BreadcrumbPage.displayName = "BreadcrumbPage"

/* ── BreadcrumbSeparator ─────────────────────────────────── */

export interface BreadcrumbSeparatorProps
  extends
    React.ComponentPropsWithoutRef<"li">,
    VariantProps<typeof breadcrumbSeparatorVariants> {}

const BreadcrumbSeparator = React.forwardRef<
  HTMLLIElement,
  BreadcrumbSeparatorProps
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    aria-hidden="true"
    className={cn(breadcrumbSeparatorVariants(), className)}
    {...props}
  >
    <ChevronRight />
  </li>
))

BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

/* ── Exports ─────────────────────────────────────────────── */

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbButton,
  BreadcrumbPage,
  BreadcrumbSeparator,
  breadcrumbListVariants,
  breadcrumbItemVariants,
  breadcrumbLinkVariants,
  breadcrumbPageVariants,
  breadcrumbSeparatorVariants,
}
